import { Maximize2 } from "react-feather";
import {
  useState,
  cloneElement,
  isValidElement,
  ReactNode,
  ReactElement,
} from "react";

export interface MaximizeProps {
  title: string;
  children?: ReactNode;
}

const Maximize: React.FC<MaximizeProps> = ({ title, children }) => {
  const [isMaximized, setIsMaximized] = useState(false);

  const removeHeightClasses = (child: ReactElement<any>): ReactElement => {
    const classNames = (child.props.className ?? "")
      .split(" ")
      .filter(
        (className: string) =>
          !className.includes("h-") &&
          !className.includes("height") &&
          !className.includes("max-h-")
      )
      .join(" ");

    return cloneElement(child, {
      ...child.props, // Spread original props to avoid type conflicts
      className: classNames,
      style: {
        ...child.props.style,
        height: isMaximized ? "100%" : child.props.style?.height,
      },
    });
  };

  const renderChildren = (): ReactNode => {
    if (!children) return null;
    if (isValidElement(children))
      return removeHeightClasses(children as ReactElement<any>);
    return children;
  };

  return (
    <>
      <div className="w-full bg-white border-2 border-primary mt-6 mr-6 overflow-hidden rounded-lg">
        <div className="border-b-2 border-b-primary flex flex-row justify-between bg-primary p-4">
          <h2 className="text-xl font-bold text-white">{title}</h2>
          <button
            className="p-2 rounded bg-white hover:bg-gray-100 border-none transition-colors"
            onClick={() => setIsMaximized(true)}
          >
            <Maximize2 width={18} className="text-primary" />
          </button>
        </div>
        <div className="overflow-hidden">
          {!isMaximized ? children : renderChildren()}
        </div>
      </div>

      {isMaximized && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white w-11/12 h-5/6 rounded-xl p-6 flex flex-col border-2 border-primary overflow-hidden">
            <div className="flex justify-between items-center mb-4 bg-primary -m-6 p-6 rounded-t-lg">
              <h2 className="text-2xl font-bold text-white">{title}</h2>
              <button
                className="w-8 h-8 rounded-full bg-white hover:bg-gray-100 border-none text-primary transition-colors flex items-center justify-center"
                onClick={() => setIsMaximized(false)}
              >
                ✕
              </button>
            </div>
            <div className="flex-grow overflow-hidden mt-6">
              {renderChildren()}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Maximize;
